import {
  couponsService,
  type FindAllCouponsQueryDto,
  type FindCouponGeneratedRevenueQueryDto,
} from '@/api/coupons.service';
import type { CreateCouponDto, UpdateCouponDto } from '@/api/coupons.service';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from '@vueuse/core';
import { toRef } from '@vueuse/core';
import type { MutationDefaultsContext } from './query-client';
import { LEDGER_ENTRIES_QUERY_KEY } from './use-ledger';
import { PAYMENTS_QUERY_KEY } from './use-payments';
import { BOOKINGS_QUERY_KEY } from './use-bookings';
import type { ApplyCouponDto, CreateLegacyCouponPaymentDto } from '@carvia/ros-client-types';
import { toValue } from 'vue';

const QUERY_KEY = 'coupons';

export const useCoupon = (id: MaybeRefOrGetter<string | undefined>) => {
  return useQuery({
    queryKey: [QUERY_KEY, toRef(id)],
    enabled: () => !!toValue(id),
    queryFn: () => couponsService.getOne(toRef(id).value!),
  });
};

export const useCouponGeneratedRevenue = (
  id: MaybeRefOrGetter<string | undefined>,
  query: MaybeRefOrGetter<FindCouponGeneratedRevenueQueryDto> = {},
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'generated-revenue', toRef(id), toRef(query)],
    enabled: () => !!toValue(id) && toValue(enabled),
    queryFn: () => couponsService.getGeneratedRevenue(toValue(id)!, toValue(query)),
  });
};

export const useCouponsWithQuery = (
  query: MaybeRefOrGetter<FindAllCouponsQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      couponsService.getAllWithQuery({
        page: pageParam,
        ...toValue(query),
      }),
    enabled: toRef(enabled),
    getNextPageParam: (lastPage) => lastPage?.meta?.nextPage ?? undefined,
    initialPageParam: 1,
  });
};

export const useUpdateCoupon = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; update: UpdateCouponDto }) =>
      couponsService.update(params.id, params.update),
    onMutate: () =>
      ({
        successMessage: 'Coupon updated successfully',
        invalidateQueryKeys: [QUERY_KEY],
      }) satisfies MutationDefaultsContext,
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      onSuccess?.();
    },
  });
};

export const useDeleteCoupon = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => couponsService.remove(id),
    onMutate: () =>
      ({
        successMessage: 'Coupon deleted successfully',
        invalidateQueryKeys: [QUERY_KEY],
      }) satisfies MutationDefaultsContext,
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      onSuccess?.();
    },
  });
};

export const useCreateCoupon = () => {
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (coupon: CreateCouponDto) => couponsService.create(coupon),
    onMutate: () =>
      ({
        successMessage: 'Coupon created successfully',
        invalidateQueryKeys: [QUERY_KEY],
      }) satisfies MutationDefaultsContext,
  });
};

export const useApplyCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY, 'apply'],
    mutationFn: ({ bookingId, couponCode, dryRun }: ApplyCouponDto) =>
      couponsService.applyCoupon({ bookingId, couponCode, dryRun }),
    onMutate: (variables) => {
      if (variables.dryRun) return;
      return {
        successMessage: 'Coupon applied successfully',
      } satisfies MutationDefaultsContext;
    },
    onSuccess: (result, variables, onMutateResult, context) => {
      if (variables.dryRun) return;
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      const bookingId = variables.bookingId;
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY, bookingId] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
    },
  });
};

export const useRemoveCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY, 'remove'],
    mutationFn: (dto: { bookingId: string; couponId: string }) => couponsService.removeCoupon(dto),
    onMutate: () =>
      ({
        successMessage: 'Coupon removed successfully',
      }) satisfies MutationDefaultsContext,
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY, variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
    },
  });
};

export const useCreateLegacyCouponPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY, 'legacy-coupon'],
    mutationFn: (dto: CreateLegacyCouponPaymentDto) =>
      couponsService.createLegacyCouponPayment(dto),
    onMutate: () =>
      ({
        successMessage: 'Legacy voucher payment recorded',
      }) satisfies MutationDefaultsContext,
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY, variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
    },
  });
};
